import { ImageName } from "@mcleod/images";
import {
    AddType, BlurEvent, Button, ChangeEvent, ClickEvent, DataDisplayEvent, DataSource, DataSourceAction,
    DataSourceExecutionEvent, DataSourceMode, DropdownSelectionEvent, Image, LookupModelSearchEvent,
    LookupModelSelectionEvent, Switch, TableRow, TableRowDisplayEvent, Textbox, TableContentsChangedEvent
} from "@mcleod/components";
import { Alignment, Api, CompanySettings, Currency, CurrencySettings, CurrencyUtil, ModelRow } from "@mcleod/core";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { AssetUtil, getDispatchControlBoolean } from "@mcleod/general";
import { ModelEdiorderProfile } from "@mcleod/datafusion";
import { MovementCarrierPay } from "@mcleod/powerbroker";
import { AutogenLayoutOrderRates } from "./autogen/AutogenLayoutOrderRates";
import { OtherChargesUtil } from "./OtherChargesUtil";
import { SuggestedPricing, SuggestedPricingProps } from "./SuggestedPricing";
import { DistanceAlertUtil } from "./DistanceAlertUtil";
import { RateUtil } from "./RateUtil";
import { TaxUtil } from "./TaxUtil";
import { getExciseTaxControlBoolean } from "../../ar/src/models/ModelExciseTaxControl";
import { ModelCustomer } from "@mcleod/ar";
import { ChargeTaxUtil } from "./ChargeTaxUtil";

export class OrderRates extends AutogenLayoutOrderRates {
    private _afterTariffRating: boolean = false;

    public get aftertariffrating(): boolean {
        return this._afterTariffRating;
    }

    public set aftertariffrating(value: boolean) {
        this._afterTariffRating = value;
    }

    private _allocationEnabled: boolean;
    private _calculatingCharges = false;
    private chargePanelExpanded: boolean = true;
    private ratingDetailsPanelExpanded: boolean = false;
    private estimateFuelSurcharge: boolean = getDispatchControlBoolean("est_fuel_surcharge")
    private autoRate: boolean = getDispatchControlBoolean("auto_rate");
    private autoCalcMaxPay: boolean = getDispatchControlBoolean("auto_pay_rates");
    private otherChargesUtil: OtherChargesUtil = new OtherChargesUtil();
    public taxUtil: TaxUtil = new TaxUtil();
    private chargeTaxUtil: ChargeTaxUtil = new ChargeTaxUtil();
    private _invokedFromEdi: boolean = false;
    private _currencyFromProfile: string = null;
    private _currencyTypeFromQuote: string;
    private distanceAlertUtil = new DistanceAlertUtil();
    private _searchTbModifier: (Textbox) => void;
    private taxPanelExpanded: boolean = true;

    doAfterRating: () => Promise<void> | void;
    checkPickupAndDeliveryEntered = () => true;

    override onLoad(): void {
        this.textboxCollectionMethod.items = RateUtil.getCollectionMethodDropdownItems();
        this.textboxCtrlParty.textCombined.manualAddLayout = null;
        this.buttonEstimateFuelSurcharge.visible = !this.estimateFuelSurcharge;
        this.otherChargesUtil.initialize(this);
        this.taxUtil.initialize(this);
        this.chargeTaxUtil.initialize(this);
        this.initializeRatingDetails();
        this.textboxCurrencyType.visible = CurrencySettings.getSingleton().multiCurrencyEnabled();
        if (!CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.textboxCurrencyType.defaultDataValue = CurrencySettings.getFunctionalCurrency();
        }
        this.setupSuggestedPricingTab();
        if (!CompanySettings.getSingleton().isAsset()) {
            this.tableOtherCharges.removeColumn(this.tableOtherCharges.columns.find(col => "columnHeaderDriver" === col.headingCell.id));
        }
    }


    textboxBillDistanceOnDataDisplay(event: DataDisplayEvent) {
        if (this.distanceAlertUtil.unclearedDistanceAlertsExist(this.sourceDistanceAlert)) {
            this.setBillDistanceWarnings("");
        }
    }

    setupSuggestedPricingTab() {
        this.styleSuggestedPricingTab();
        this.tabSuggestedPricing.heading.addClickListener((event) => {
            this.tabSuggestedPricingClicked(event);
        });
        this.tabSuggestedPricing["buttonExpand"].addClickListener((event) => {
            this.tabSuggestedPricingClicked(event);
        });
    }

    tabSuggestedPricingClicked(event: ClickEvent) {
        if (this.tabSuggestedPricing.expanded) {
            const stopData: ModelRow[] = this.mainDataSource.childDataSources.find((source) => source.id == "sourceStop")?.data;
            const props: SuggestedPricingProps = {
                stopData: stopData,
                billDistance: this.activeRow.get("bill_distance"),
                equipmentTypeId: this.activeRow.get("equipment_type_id"),
                orderId: this.activeRow.get("id"),
                commitmentId: this.activeRow.get("commitment_id"),
                origDestRateId: this.activeRow.get("orig_dest_rate_id")
            };
            const suggestedPricing: SuggestedPricing = this.layoutSuggestedPricing as SuggestedPricing;
            // hide Commitment/Auto Rating tab until some auto-rating work is done to overhaul commitment rating
            suggestedPricing.tabCommitmentAutoRating.visible = false;
            if (!suggestedPricing.initialize(props)) {
                this.tabSuggestedPricing.expandCollapseTab(false, false);
            }
        }
    }

    styleSuggestedPricingTab() {
        const image = new Image({
            name: ImageName.suggestedPricing,
            color: "primary",
            fontBold: true,
            rowBreak: false,
            marginRight: 5,
            fontSize: "large"
        })
        this.tabSuggestedPricing.heading.insert(image, 0);
        this.tabSuggestedPricing.heading.setProps({ color: "subtle.light", fontBold: false, borderWidth: 0 });
        this.tabSuggestedPricing.headingLabel.setProps({ color: "McLeodPrimary", fontSize: "large", fontBold: true });
    }

    async sourceOrdersAfterExecution(event: DataSourceExecutionEvent) {
        RateUtil.setAutoRateContext(this, this.activeRow.get("rate_id"), this.activeRow.get("orig_dest_rate_id"));
        await RateUtil.enableDisableUnitsAndRates(this, this.activeRow.get("rate_type"), event.getAction() != DataSourceAction.SEARCH, false, false);
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            if (this.mainDataSource.mode !== DataSourceMode.ADD) {
                const transactionalCurrency = await this.getTransactionalCurrency();
                const orderCurrencyCode = this.mainDataSource.activeRow?.get("freight_charge_c", transactionalCurrency) as string;
                this.mainDataSource.activeRow?.setLookupModelData("freight_charge_c", { currency_type_id: orderCurrencyCode });
                this.mainDataSource.displayDataInBoundComponent(this.textboxCurrencyType);
                this.otherChargesUtil.currencyType = orderCurrencyCode;
                this.taxUtil.currencyType = orderCurrencyCode;
            }
        }
        if (event.getAction() === DataSourceAction.SEARCH) {
            const customerId = this.mainDataSource?.activeRow?.get("customer_id");
            if (customerId != null) {
                await this.sourceCustomer.search({ "id": customerId });
            }
        }
    }

    private setTaxesVisible(visible: boolean) {
        this.panelExciseTaxTotal.visible = visible;
        this.panelTotalWithTaxes.visible = visible;
        this.panelTaxes.visible = visible;
        if (!visible) {
            this.tableOtherCharges.removeColumn("columnHeaderTaxable");
        }
        this.setExciseTaxableOptions();
    }

    private setExciseTaxableOptions() {
        const isExciseTaxable: boolean = this.activeRow.getBoolean("excise_taxable", false);
        const isExciseDisableUpdate: boolean = this.activeRow.getBoolean("excise_disable_update", false);
        this.tableTax.allowEdit = isExciseTaxable;
        this.tableTax.addType = isExciseTaxable ? AddType.QUICK : AddType.NOT_ALLOWED;
        this.tableTax.allowDelete = isExciseTaxable;
        this.tableTax.displayToolsCell = isExciseTaxable;
        this.sourceExcisetaxCharge.displayDataInBoundComponents();
        this.buttonCreateTax.enabled = isExciseTaxable && !isExciseDisableUpdate;
    }

    setMaxTargetPayData() {
        const maxPayData = this.mainDataSource.activeRow.get("max_target_pay_data");
        if (maxPayData != null && this.carrierPayLayout?.visible == true) {
            this.setupMaxTargetPanel(true);
            this.carrierPayLayout.setDataFromResponse(maxPayData);
            this.mainDataSource.activeRow.set("max_target_pay_data", undefined);
        }
    }

    getOtherChargeDatasource(): DataSource {
        return this.sourceOrderOtherCharge;
    }

    get carrierPayLayout(): MovementCarrierPay {
        return this.layoutMovementRates as MovementCarrierPay
    }

    set calculatingCharges(value: boolean) {
        this._calculatingCharges = value;
    }

    get calculatingCharges(): boolean {
        return this._calculatingCharges;
    }

    get currencyTypeFromQuote(): string {
        return this._currencyTypeFromQuote;
    }

    set currencyTypeFromQuote(currencyTypeFromQuote: string) {
        this._currencyTypeFromQuote = currencyTypeFromQuote;
    }

    public getTaxTable() {
        return this.tableTax;
    }

    textboxSegAllocCodeBeforeLookupModelSearch(event) {
        event.filter.alloc_type = 'M';
        event.filter.is_active = 'Y';
    }

    buttonExpandChargesOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.chargePanelExpanded) this.panelOtherChargeTable.collapse(transitionProps).then(() => {
            this.buttonExpandCharges.imageRotation = 90
        }); else this.panelOtherChargeTable.expand(transitionProps).then(() => {
            this.buttonExpandCharges.imageRotation = 0
        });
        this.chargePanelExpanded = !this.chargePanelExpanded;
    }

    buttonExpandDetailsOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.ratingDetailsPanelExpanded) this.panelRatingDetailsTable.collapse(transitionProps).then(() => {
            this.buttonExpandDetails.imageRotation = 90
        }); else this.panelRatingDetailsTable.expand(transitionProps).then(() => {
            this.buttonExpandDetails.imageRotation = 0
        });
        this.ratingDetailsPanelExpanded = !this.ratingDetailsPanelExpanded;
    }

    async sourceOrdersAfterModeChange(event) {
        if ((event.newMode == DataSourceMode.ADD || event.newMode == DataSourceMode.UPDATE) && event.newMode != event.oldMode) {
            this.setupMaxTargetPanel(true);
            this.sourceOrderOtherCharge.mode = DataSourceMode.ADD;
            this.checkSegAllocEnabled();
            if (CurrencySettings.getSingleton().multiCurrencyEnabled() && getExciseTaxControlBoolean("is_active", false)) {
                this.setTaxesVisible(true);
            } else {
                this.setTaxesVisible(false);
                this.tableOtherCharges.removeColumn("columnHeaderTaxable");
                this.tableOtherCharges.removeColumn("columnHeaderBackhaulTax");
            }
        }
        if (event.newMode === DataSourceMode.SEARCH && CurrencySettings.getSingleton().multiCurrencyEnabled() && getExciseTaxControlBoolean("is_active", false)) {
            this.panelTaxes.visible = true;
        }
        RateUtil.setRateListeners(this);
        if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
            CurrencyUtil.configureCurrencySelector(this.textboxCurrencyType);
        }
    }

    private setupMaxTargetPanel(visible: boolean) {
        this.carrierPayLayout.visible = !AssetUtil.isAssetMoveType(this.sourceOrders)
            && (this.activeRow == null || !this.activeRow.getBoolean("multi_movement", false))
            && visible;
        this.panelRateTotals.borderBottomRightRadius = visible ? 0 : 4;
        this.panelRateTotals.borderBottomLeftRadius = visible ? 0 : 4;
        if (this.carrierPayLayout.mainDataSource.activeRow == null) this.carrierPayLayout.configureForOrderEntry(this.mainDataSource);
    }

    private checkSegAllocEnabled() {
        this._allocationEnabled = this.sourceOrders.activeRow?.get("seg_alloc_enabled");
        delete this.sourceOrders.activeRow?.data["seg_alloc_enabled"];
        this.textboxSegAllocCode.visible = this._allocationEnabled;
        this.textboxSegAllocCode.required = this._allocationEnabled;
        if (!this._allocationEnabled) this.textboxSegAllocCode.dataSource = null;
    }

    async otherChargesOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        await this.otherChargesUtil.otherChargeOnBlur(event);
    }

    buttonEstimateFuelSurchargeOnClick(event: ClickEvent) {
        this.ratingButtonClicked(event, false, true);
    }

    buttonRateOnClick(event: ClickEvent) {
        this.ratingButtonClicked(event, true);
    }

    ratingButtonClicked(event: ClickEvent, autoRate: boolean, estimateFsc: boolean = this.estimateFuelSurcharge) {
        const button = event.target as Button;
        button.busy = true;
        this.calculateRates(autoRate, estimateFsc).finally(() => {
            button.busy = false
        });
    }

    buttonAvailableCreditOnClick(event: ClickEvent) {
        this.buttonAvailableCredit.busy = true;
        const row = this.mainDataSource.activeRow;
        const params = { customer_id: this.mainDataSource.activeRow.get("customer_id") };
        if (row.get("total_charge") != null) this.otherChargesUtil.addCurrencyToProps(params, row, "total_charge", "order_total_charges");
        if (row.get("totalcharge_and_excisetax") != null) this.otherChargesUtil.addCurrencyToProps(params, row, "totalcharge_and_excisetax", "order_total_charges_plus_excisetax");
        if (this.mainDataSource.mode != DataSourceMode.ADD && row.get("id") != null) params["exclude_order_id"] = row.get("id");

        Api.search("lme/ar/customer-balance", params).then(result => {
            const customerData = result?.data?.[0];
            if (customerData["available_credit"] == null) this.labelAvailableCredit.text = ""; else this.labelAvailableCredit.text = CurrencyUtil.formatCurrency(customerData["available_credit"]);
        }).finally(() => this.buttonAvailableCredit.busy = false);
    }

    async textboxChargeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        if (event.hasSelection() === true)
            await this.otherChargesUtil.textboxChargeIdOnLookupModelSelection(event, this.mainDataSource.activeRow.get("id"));
    }

    async tableOtherChargesOnContentsChanged(event: TableContentsChangedEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH || this.aftertariffrating) return;
        await this.otherChargesUtil.tableOtherChargesOnContentsChanged();
        await this.calculateRates(false, false, false);
    }

    async calculateRates(autorate: boolean = this.autoRate, estimateFsc: boolean = this.estimateFuelSurcharge, affectRateId: boolean = true): Promise<any> {
        if (!this.mainDataSource.isAddingOrUpdating() || this.calculatingCharges || this.mainDataSource.activeRow == null || !this.checkPickupAndDeliveryEntered()) return Promise.resolve();
        const orderRow: ModelRow = this.mainDataSource.getDataboundValues(null, true, null, null);
        const stopRows: ModelRow[] = this.mainDataSource.childDataSources.find((source) => source.id == "sourceStop")?.data
        orderRow.set("def_move_type", this.mainDataSource.activeRow.get("def_move_type"));
        orderRow.set("id", this.mainDataSource.activeRow.get("id"));
        this.mainDataSource.setComponentsBusy(true);
        this.calculatingCharges = true;
        const calcMaxTargetPay = this.shouldCalcMaxPay();
        try {
            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                orderRow.set("freight_charge", this.activeRow.get("freight_charge"));
            }
            const body = {
                "autorate": autorate, "estimate_fsc": estimateFsc,
                "order_row": orderRow, "calculate_max_target": calcMaxTargetPay
            };
            const response = await Api.search("lme/dispatch/calculate-order-charges", body);

            let totalChargeChanged = false;
            const data = response.data[0];
            if (!data) return;

            const orderApiData = data["order_row"] || [];
            if (data.tariff_rated && this.mainDataSource.activeRow?.get("id")) {
                this.aftertariffrating = true;
                await this.sourceOrderOtherCharge?.search?.({ order_id: this.mainDataSource.activeRow?.get("id") });
                await this.sourceRatingDetails?.search?.({ order_id: this.mainDataSource.activeRow?.get("id") });
                await this.sourceRatingDetailsSummary?.search?.({ order_id: this.mainDataSource.activeRow?.get("id") });
                this.tableRevSummary.displayComponentData(null, this.sourceRatingDetailsSummary.data, 0);
                this.tableRevPerFgi.displayComponentData(null, this.sourceRatingDetails.data, 0);
                this.tableOtherCharges.displayComponentData(null, this.sourceOrderOtherCharge.data, 0);
                this.mainDataSource.activeRow.set("rate_type", orderApiData["rate_type"]);
                this.mainDataSource.activeRow.set("rate_unit_desc", orderApiData["rate_unit_desc"]);
                this.mainDataSource.activeRow.set("rate_units", orderApiData["rate_units"]);
                this.mainDataSource.activeRow.set("rate", orderApiData["rate"]);
                this.mainDataSource.activeRow.set("freight_charge", orderApiData["freight_charge"]);
                this.mainDataSource.activeRow.set("freight_charge_n", orderApiData["freight_charge_n"]);
                this.mainDataSource.activeRow.set("otherchargetotal", orderApiData["otherchargetotal"]);
                this.mainDataSource.activeRow.set("otherchargetotal_n", orderApiData["otherchargetotal_n"]);
                this.mainDataSource.activeRow.set("total_charge", orderApiData["total_charge"]);
                this.mainDataSource.activeRow.set("total_charge_n", orderApiData["total_charge_n"]);
                this.aftertariffrating = false;
                return;
            }

            const errorMessage: string = data?.["message"];
            if (errorMessage) {
                this.setBillDistanceWarnings(errorMessage);
            } else {
                this.clearBillDistanceWarnings();
            }

            if (stopRows != null) {
                const stopApiData = data["stop_data"] || [];
                stopApiData.forEach((stop, index) => {
                    if (stopRows[index]) {
                        stopRows[index].set("rate_dist_from_previous", stop["rate_dist_from_previous"]);
                    }
                });
            }

            // Estimate Fuel Surcharge
            if (estimateFsc) {
                for (let i = this.tableOtherCharges.rows.length - 1; i >= 0; i--) {
                    const row = this.tableOtherCharges.rows[i];
                    if (row?.data?.data["est_fuel_surcharge"] === "Y") {
                        row.deleteRow()
                    }
                }
            }

            // Auto Rate
            if (autorate) {
                for (let i = this.tableOtherCharges.rows.length - 1; i >= 0; i--) {
                    const row = this.tableOtherCharges.rows[i];
                    if (row?.data?.data["rate_id"] != null) {
                        row.deleteRow()
                    }
                }
            }

            // Keep existing rows so their original data survives the rating refresh. Recreating an existing,
            // non-appending ModelRow loses that baseline, causing Save to omit the row from _child_links.
            const existingOtherCharges = new Map<string, ModelRow>();
            this.sourceOrderOtherCharge.data.forEach(row => {
                const id = row.get("id") as string;
                if (row._appending === false && id != null) {
                    existingOtherCharges.set(id, row);
                }
            });
            this.sourceOrderOtherCharge.data = [];
            const otherCharges = data["other_charges"] || [];
            otherCharges.forEach(element => {
                const modelRow = existingOtherCharges.get(element["id"])
                    ?? new ModelRow(this.sourceOrderOtherCharge.url, true);
                modelRow.setValues(element);
                this.sourceOrderOtherCharge.data.push(modelRow);
            });
            this.tableOtherCharges.displayData(null, this.sourceOrderOtherCharge.data, 0);
            await this.otherChargesUtil.tableOtherChargesOnContentsChanged();

            this.textboxRateType.setSelectedItemFromValue(orderApiData["rate_type"]);
            this.mainDataSource.activeRow.set("rate_type", orderApiData["rate_type"]);
            this.mainDataSource.activeRow.set("bill_distance", orderApiData["bill_distance"]);
            this.mainDataSource.activeRow.set("autorate_status", orderApiData["autorate_status"]);
            this.mainDataSource.activeRow.set("rate", orderApiData["rate"]);
            this.mainDataSource.activeRow.data["calc_method"] = data["rate_type"];
            this.mainDataSource.activeRow.set("rate_units", orderApiData["rate_units"]);
            this.activeRow.updateCurrency("freight_charge", orderApiData["freight_charge"]);
            if (orderApiData["total_charge"] != null) {
                if (orderApiData["total_charge"].amount != this.mainDataSource.activeRow.get("total_charge")?.amount || 0) {
                    this.mainDataSource.activeRow.set("total_charge", orderApiData["total_charge"]);
                    this.mainDataSource.activeRow.set("total_charge_n", orderApiData["total_charge_n"]);
                    totalChargeChanged = true;
                }
            }
            this.mainDataSource.activeRow.set("otherchargetotal", await this.otherChargesUtil.getOtherChargeTotal(false));

            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                const exciseTaxes = data["excise_taxes"] || [];
                await this.updateExciseTaxes(exciseTaxes)
                const orderCurrency = this.mainDataSource.activeRow?.get("freight_charge_c", await this.getTransactionalCurrency()) as string;
                await this.convertPricingCurrencies(orderCurrency);
            }

            const previousTotalChargeAndExciseTax = this.mainDataSource.activeRow.getCurrency("totalcharge_and_excisetax")?.amount;
            this.mainDataSource.activeRow.set("excisetax_total", this.taxUtil.getTaxChargeBillTotal(false));
            this.mainDataSource.activeRow.set("totalcharge_and_excisetax", this.taxUtil.getTaxChargeBillTotal(true));
            const totalChargeAndExciseTaxChanged = previousTotalChargeAndExciseTax != this.mainDataSource.activeRow.getCurrency("totalcharge_and_excisetax")?.amount;

            if (affectRateId) {
                RateUtil.setAutoRateContext(this, orderApiData["rate_id"], orderApiData["orig_dest_rate_id"]);
            }
            if (calcMaxTargetPay) this.carrierPayLayout.setDataFromResponse(data.max_target_pay_data);
            if ((totalChargeChanged || totalChargeAndExciseTaxChanged) && this.doAfterRating != null) this.doAfterRating();
        } finally {
            this.calculatingCharges = false;
            this.mainDataSource.setComponentsBusy(false);
        }
    }

    /**
     * Sets the warning state for the bill distance textbox based on the response from the Model.
     * If there is an error message in the response, it sets the textbox properties to display a warning.
     * Otherwise, it resets the properties to default values.
     *
     * @param errorMessage
     */
    private setBillDistanceWarnings(errorMessage: string): void {
        this.textboxBillDistance.color = "warning";
        this.textboxBillDistance.borderColor = "warning";
        this.textboxBillDistance.borderWidth = 1;
        this.textboxBillDistance.tooltip = errorMessage;
        this.textboxBillDistance.text = "0.0";
        this.textboxBillDistance.allowTooltip = true;
    }

    /**
     * Clears any warning state for the bill distance textbox.
     * Resets the textbox properties such as color, border, and tooltip to their default values.
     */
    private clearBillDistanceWarnings(): void {
        this.textboxBillDistance.color = "default";
        this.textboxBillDistance.borderColor = undefined;
        this.textboxBillDistance.borderWidth = undefined;
        this.textboxBillDistance.tooltip = undefined;
        this.textboxBillDistance.allowTooltip = false;
    }

    shouldCalcMaxPay(): boolean {
        return this.autoCalcMaxPay && this.carrierPayLayout?.visible && (this.activeRow.get("override_max_pay") == null || this.activeRow.get("override_targetpay") == null)
    }

    /** This is an event handler for the onChange event of checkboxLockMiles.  */
    checkboxLockMilesOnChange(event) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        this.textboxBillDistance.enabled = event.newValue === false;
    }

    sourceRatingDetailsAfterExecution(event: DataSourceExecutionEvent) {
        this.initializeRatingDetails();
    }

    /** This is an event handler for the afterExecution event of sourceDistanceAlert. */
    sourceDistanceAlertAfterExecution(event: DataSourceExecutionEvent) {
        this.distanceAlertUtil.checkForDistanceAlertsTextbox(this.sourceDistanceAlert, this.textboxBillDistance);
        this.textboxBillDistanceOnDataDisplay(null);
    }

    initializeRatingDetails() {
        this.switchByFreightItem.checked = true;
        this.tableRevSummary.visible = false;
        this.panelRatingDetailsTable.collapse({ speed: 100, paddingTop: 0 }).then(() => {
            this.buttonExpandDetails.imageRotation = 90;
        });
    }

    switchByFreightItemOnClick(event: ClickEvent) {
        const switchByFreightItem = event.target as Switch;
        this.tableRevPerFgi.visible = switchByFreightItem.checked;
        this.tableRevSummary.visible = !switchByFreightItem.checked;
    }

    tableRevPerFgiOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow() as TableRow;
        this.addBuySellTooltips(tableRow, "textboxFgiRevenueAmount");
    }

    tableRevSummaryOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow() as TableRow;
        this.addBuySellTooltips(tableRow, "textboxRevenueAmount");
    }

    addBuySellTooltips(tableRow: TableRow, componentId: string) {
        const modelRow: ModelRow = tableRow.data as ModelRow;
        const buyRateModelRow = new ModelRow(modelRow._modelPath, false, modelRow.data);
        buyRateModelRow.set("charge_as_calc_description", modelRow.get("buy_rate_description"));
        buyRateModelRow.set("tariff_id", modelRow.get("tariff_id"));
        buyRateModelRow.set("tli_uid", modelRow.get("tli_uid"));
        if (buyRateModelRow.get("tariff_id", null) != null) {
            const component = tableRow.findComponentById(componentId);
            if (component != null) {
                CommonTooltips.setTooltipContentLayout(component, buyRateModelRow,
                    "lme/dispatch/RevenueDetailQuickInfo");
                component.tooltipDynamicOptions = { position: Alignment.TOP };
            }
        }
    }

    async textboxExciseTaxIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        if (event.hasSelection() === true) {
            await this.taxUtil.textboxExciseTaxIdOnLookupModelSelection(event);
        }
    }

    async taxesOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        await this.taxUtil.taxOnBlur(event)
    }

    tableTaxOnContentsChanged(event) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        this.taxUtil.tableTaxOnContentsChanged();
        this.doAfterRating();
    }

    async taxMethodAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        await this.taxUtil.taxOnDropdownSelection(event);
    }

    buttonExpandTaxesOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.taxPanelExpanded) {
            this.panelTaxTable.collapse(transitionProps).then(() => {
                this.buttonExpandTaxes.imageRotation = 90
            });
        } else {
            this.panelTaxTable.expand(transitionProps).then(() => {
                this.buttonExpandTaxes.imageRotation = 0
            });
        }
        this.taxPanelExpanded = !this.taxPanelExpanded;
    }

    getTaxDatasource(): DataSource {
        return this.sourceExcisetaxCharge;
    }

    /**
     * Creates tax rows based on current order and associated billing using server-side logic.
     * Mirrors BillingRates behavior but locates the billing record from the order first.
     */
    async createTaxOnClick() {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH || !this.buttonCreateTax.enabled) return;
        this.buttonCreateTax.busy = true;
        try {
            await this.updateExciseTaxes(null, true);
        } finally {
            this.buttonCreateTax.busy = false;
        }
    }

    /**
     * Updates excise taxes for the current order.
     * Delegates to TaxUtil for the actual implementation.
     *
     * @param taxesList - Optional list of tax rows to use instead of fetching from server
     * @param showDialog - Whether to show error dialogs to the user
     */
    public async updateExciseTaxes(taxesList?: ModelRow[], showDialog: boolean = false): Promise<void> {
        await this.taxUtil.updateExciseTaxes(taxesList, showDialog, "O");
    }

    async textboxChargeCalcMethodOnDropdownSelection(event: DropdownSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const tableRow = TableRow.getContainingTableRow(event.target as Textbox);
        const weight = this.sourceOrders.activeRow.get("weight", null);
        const freightCharge = this.sourceOrders.activeRow.get("freight_charge", new Currency(0)) as Currency;
        if (event.selection?.value === "D") {
            const distance = this.sourceOrders.activeRow.get("bill_distance", null);
            this.setChargeDistanceUnits(tableRow, distance);
        } else if ("F" === event.selection?.value) {
            tableRow.data.set("units", 1)
        } else if ("C" === event.selection?.value) {
            tableRow.data.set("units", weight / 100);
        } else if ("T" === event.selection?.value) {
            tableRow.data.set("units", weight / 2000);
        } else if ("P" === event.selection?.value) {
            tableRow.data.set("units", freightCharge.base_amount);
        }
        const textbox: Textbox = event.target as Textbox;
        await this.otherChargesUtil.calculateOtherCharges({
            otherChargeRow: textbox.boundRow,
            otherChargeTextbox: textbox
        }, false);
    }

    setChargeDistanceUnits(tableRow: TableRow, distance: string) {
        const units = tableRow.findComponentById("textboxChargeUnits") as Textbox;
        tableRow.data.set("units", distance)
        const rate = tableRow.data.get("rate");
        if (rate != null) {
            const amount = tableRow.findComponentById("textboxChargeAmount") as Textbox;
            amount.text = (parseFloat(rate) * parseFloat(distance)).toFixed(2);
        }
    }

    async textboxCurrencyTypeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const selectedRow = event.selection;
        if (selectedRow) {
            const currencyType = selectedRow.get("currency_type_id", await this.getTransactionalCurrency()) as string;
            this.activeRow.getCurrency("freight_charge")?.setCurrencyCode(currencyType);
            await this.convertPricingCurrencies(currencyType);
            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                // recalculate totals because the conversion rates for charges with a customer are likely different for the new currency
                await this.calculateRates(false, false, false);
            }
        }
    }

    async convertPricingCurrencies(currencyType: string) {
        this.otherChargesUtil.currencyType = currencyType;
        const freightCharge: Currency = this.mainDataSource.activeRow?.getCurrency("freight_charge");
        freightCharge?.setCurrencyCode(currencyType);
        freightCharge?.setConversionDate(freightCharge.getConversionDate());
        const otherCharge: Currency = this.mainDataSource.activeRow?.getCurrency("otherchargetotal");
        otherCharge?.setCurrencyCode(currencyType);
        const totalCharge: Currency = this.mainDataSource.activeRow?.getCurrency("total_charge");
        totalCharge?.setCurrencyCode(currencyType);
        const totalTaxes: Currency = this.mainDataSource.activeRow?.getCurrency("excisetax_total");
        totalTaxes?.setCurrencyCode(currencyType);
        const totalFreightChargeWithTaxes: Currency = this.mainDataSource.activeRow?.getCurrency("totalcharge_and_excisetax");
        totalFreightChargeWithTaxes?.setCurrencyCode(currencyType);
        this.mainDataSource.activeRow?.setValues({
            "freight_charge": freightCharge,
            "otherchargetotal": otherCharge,
            "total_charge": totalCharge,
            "excisetax_total": totalTaxes,
            "totalcharge_and_excisetax": totalFreightChargeWithTaxes
        });
        this.updateCurrencySelector(currencyType);
        for (const tableRow of this.tableOtherCharges.rows) {
            const modelRow: ModelRow = tableRow.data;
            const amount: Currency = modelRow.getCurrency("amount");
            if (amount?.getAmount() != null) {
                if (!modelRow.isNull("customer_id")) {
                    const customer = await new ModelCustomer().searchSingle({ id: modelRow.get("customer_id") });
                    amount.setCurrencyCode(customer?.data?.currency_type || currencyType);
                } else {
                    amount.setCurrencyCode(currencyType);
                }
                modelRow.updateCurrency("amount", amount);
                tableRow.displayComponentValues();
            }
        }
        const quickAddDataOtherCharges: ModelRow = this.tableOtherCharges.quickAddData;
        if (quickAddDataOtherCharges) {
            const amount: Currency = quickAddDataOtherCharges.getCurrency("amount");
            if (amount?.getAmount() != null) {
                if (!quickAddDataOtherCharges.isNull("customer_id")) {
                    const customer = await new ModelCustomer().searchSingle({ id: quickAddDataOtherCharges.get("customer_id") });
                    amount.setCurrencyCode(customer?.data?.currency_type || currencyType);
                } else {
                    amount.setCurrencyCode(currencyType);
                }
                quickAddDataOtherCharges.updateCurrency("amount", amount);
                this.tableOtherCharges.quickAddRow.displayComponentValues();
            }
        }
        for (const tableRow of this.tableTax.rows) {
            const modelRow: ModelRow = tableRow.data;
            const amount: Currency = modelRow.getCurrency("amount");
            if (amount?.getAmount() != null) {
                amount.setCurrencyCode(currencyType);
                modelRow.updateCurrency("amount", amount);
                tableRow.displayComponentValues();
            }
        }
        const quickAddDataTax: ModelRow = this.tableTax.quickAddData;
        if (quickAddDataTax) {
            const amount: Currency = quickAddDataTax.getCurrency("amount");
            if (amount?.getAmount() != null) {
                amount.setCurrencyCode(currencyType);
                quickAddDataTax.updateCurrency("amount", amount);
                this.tableTax.quickAddRow.displayComponentValues();
            }
        }
    }

    async getTransactionalCurrency(): Promise<string> {
        let transactionCurrency = CurrencySettings.getTransactionalCurrency();
        const partner = this.mainDataSource.activeRow?.getString("partner_id");
        const version = this.mainDataSource.activeRow?.getString("partner_version");
        if (this.getInvokedFromEdi() && this.getCurrencyFromProfile()) {
            transactionCurrency = this.getCurrencyFromProfile();
        } else if (partner && version) {
            const profile = await new ModelEdiorderProfile().searchSingle({
                partner_id: partner,
                version: version
            });
            if (profile.data.currency_type_id) {
                transactionCurrency = profile.data.currency_type_id;
            }
        }
        return transactionCurrency;
    }

    public set invokedFromEdi(value: boolean) {
        this._invokedFromEdi = value;
    }

    public getInvokedFromEdi(): boolean {
        return this._invokedFromEdi;
    }

    public set currencyFromProfile(value: string) {
        this._currencyFromProfile = value;
    }

    public getCurrencyFromProfile(): string {
        return this._currencyFromProfile;

    }

    private updateCurrencySelector(currencyCode: string) {
        this.mainDataSource.activeRow?.set("freight_charge_c", currencyCode);
        this.mainDataSource.activeRow?.setLookupModelData("freight_charge_c", { currency_type_id: currencyCode });
        this.mainDataSource.displayDataInBoundComponent(this.textboxCurrencyType);
    }

    set searchTbModifier(value: (Textbox) => void) {
        this._searchTbModifier = value;
    }

    /** This is an event handler for the onRowDisplay event of tableOtherCharges. */
    tableOtherChargesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow() as TableRow;

        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            this.applySearchModeModifications(tableRow);
        } else {
            this.chargeTaxUtil.handleTableRowDisplay(tableRow);
        }
    }

    /**
     * Applies modifications to textboxes in search mode
     */
    private applySearchModeModifications(tableRow: TableRow) {
        if (this._searchTbModifier) {
            tableRow.findComponentsByType(Textbox)
            .forEach(textbox => this._searchTbModifier(textbox));
        }
    }

    /**
     * Handler for when switchTaxable changes
     */
    private onSwitchTaxableChange(event: ChangeEvent) {
        this.chargeTaxUtil.onSwitchTaxableChange(event);
    }

    /**
     * Handler for when switchBackhaulTax changes
     */
    private onSwitchBackhaulTaxChange(event: ChangeEvent) {
        this.chargeTaxUtil.onSwitchBackhaulTaxChange(event);
    }

    beforeTextboxStopLookupModelSearch(event: LookupModelSearchEvent) {
        // Prevent search if no active order id
        if (!this.sourceOrders?.activeRow?.get("id")) {
            event.preventDefault?.();
            return;
        }

        event.filter.order_id = this.sourceOrders.activeRow.get("id");
        event.filter.stop_type = "in PU,SO";
    }

    textboxStopNumberOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true)
            this.otherChargesUtil.textboxStopNumberOnLookupModelSelection(event);
    }

    textboxCustomerOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target);
        const modelRow: ModelRow = tableRow.data as ModelRow;
        const switchIncludeInFreight = tableRow.findComponentById("switchIncludeInFreight") as Switch;
        if (switchIncludeInFreight) {
            switchIncludeInFreight.enabled = !event.hasSelection();
        }
        if (event.hasSelection()) {
            switchIncludeInFreight.checked = false;
            const amount = modelRow.getCurrency("amount");
            amount.setCurrencyCode(event.selection.get("currency_type"));
            modelRow.set("amount", amount);
            tableRow.displayComponentValues();
        }
    }

    async switchTaxableOrderOnChange(event: ChangeEvent) {
        if (this.activeRow == null || this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const isChecked = event?.newValue === true;
        const taxRows = this.tableTax.rows;
        if (!isChecked && taxRows.length > 0) {
            const message = "This change will result in a non-taxable order and will remove all excise taxes (generated or manual) currently defined for this order.\n\nProceed with tax status change for this order?";
            const confirm = event.userInitiatedChange ? await CommonDialogs.showYesNo(message) : true;
            if (confirm) {
                for (let i = taxRows.length - 1; i >= 0; i--) {
                    const row = taxRows[i];
                    this.tableTax.deleteRow(row.index);
                }

                // Recalculate totals and refresh bound controls
                this.taxUtil.tableTaxOnContentsChanged();
                this.mainDataSource.displayDataInBoundComponent(this.textboxExcisetaxTotal);
                this.mainDataSource.displayDataInBoundComponent(this.textboxTotalWithTax);
                this.setExciseTaxableOptions();
            } else {
                // Revert switch back to ON (taxable) and refresh control display
                this.switchTaxableOrder.checked = true;
                this.setExciseTaxableOptions();
            }
        } else {
            // Turning ON simply enables tax editing options
            this.setExciseTaxableOptions();
        }
    }

    textboxStateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.country_id = "in CAN,CAD"; // show only Canadian provinces
    }


    tableTaxOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow() as TableRow;
        const modelRow: ModelRow = tableRow.data as ModelRow;
        const isEstimated = modelRow.getBoolean("is_estimated", false);

        if (isEstimated) {
            // Prevent row from entering edit mode (disable double-click to edit)
            tableRow.allowEdit = false;

            // Override the delete button to show a message instead of deleting
            tableRow.deleteRow = async () => {
                await CommonDialogs.showMessage(
                    "Generated excise tax records cannot be deleted. These records are automatically calculated and managed by the system."
                );
            };
        }
    }

    async switchExciseDisableUpdateOnChange(event: ChangeEvent) {
        if (this.activeRow == null || this.mainDataSource.mode === DataSourceMode.SEARCH) return;

        const isChecked = event?.newValue === true;
        const taxRows = this.tableTax.rows.filter(row => row.data.getBoolean("is_estimated", false));

        if (isChecked && taxRows.length > 0) {
            const message = "This change will result in disabling the auto update and creation of tax records during the transfer to billing and will remove all generated excise taxes (manual taxes will remain) currently defined for this order.\n\nProceed with disable of tax auto update?";
            const confirm = event.userInitiatedChange ? await CommonDialogs.showYesNo(message) : true;
            if (confirm) {
                // Remove estimated (generated) tax rows
                for (let i = taxRows.length - 1; i >= 0; i--) {
                    const row = taxRows[i];
                    if (row.data.getBoolean("is_estimated", false)) {
                        this.tableTax.deleteRow(row.index);
                    }
                }

                // Recalculate and update totals
                this.taxUtil.tableTaxOnContentsChanged();
                this.mainDataSource.displayDataInBoundComponent(this.textboxExcisetaxTotal);
                this.mainDataSource.displayDataInBoundComponent(this.textboxTotalWithTax);
                this.setExciseTaxableOptions();
            } else {
                // Revert checkbox
                this.switchExciseDisableUpdate.checked = false;
            }
        } else {
            // Update button state when switch is toggled off or there are no tax rows
            this.setExciseTaxableOptions();
        }
    }
}
